import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash-es";
import {
  ACTIVITY_HUB_ORGANIZATION_ROLES,
  ACTIVITY_HUB_USER_TYPE,
  SUBMITTER_ROLE,
  SUBMITTER_USER_TYPE,
} from "utils/userRoleHelpers"
import { errorToast } from "shared/toast"

const SLICE_NAME = "organizationUsers"

const INITIAL_STATE = {
  activityHubUsers: [],
  loading: true,
  submitterUsers: [],
  userIdMap: {},
  users: [],
}

const buildUserIdMap = (users) => cloneDeep(users).reduce((map, user) => {
  map[user.id] = user

  return map
}, {})

// The users grouped by role are built once, when the users are loaded, rather than
// by filtering the full list of users every time a group of users is needed.
const buildActivityHubUserList = (users) => (
  users.filter((user) => ACTIVITY_HUB_ORGANIZATION_ROLES.includes(user.role))
)

const buildSubmitterUserList = (users) => users.filter((user) => user.role === SUBMITTER_ROLE)

export const loadUsers = createAsyncThunk(
  `${SLICE_NAME}/loadUsers`,
  // load expected be a function that returns a list of users
  // or throws an error if there's a problem getting the users
  async ({ load }) => {
    const newUsers = await load()

    return { newUsers }
  },
)

export const organizationUsersSlice = createSlice({
  name: SLICE_NAME,
  initialState: cloneDeep(INITIAL_STATE),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadUsers.pending, (state) => {
        state.loading = true
      })
      .addCase(loadUsers.fulfilled, (state, action) => {
        const { newUsers } = action.payload

        state.userIdMap = buildUserIdMap(newUsers)
        state.users = newUsers
        state.activityHubUsers = buildActivityHubUserList(newUsers)
        state.submitterUsers = buildSubmitterUserList(newUsers)

        state.loading = false
      })
      .addCase(loadUsers.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
  },
})

export const loading = (state) => state[SLICE_NAME].loading

export const organizationUserById = (id) => (state) => state[SLICE_NAME].userIdMap[id]

export const organizationUsers = (state) => state[SLICE_NAME].users

export const activityHubUsers = (state) => state[SLICE_NAME].activityHubUsers

export const submitterUsers = (state) => state[SLICE_NAME].submitterUsers

// The organization's users are only ever loaded as Activity Hub users and submitters,
// so both user types together are all of the organization's users.
export const usersForUserTypes = (userTypes = []) => (state) => {
  const includesActivityHubUsers = userTypes.includes(ACTIVITY_HUB_USER_TYPE)
  const includesSubmitters = userTypes.includes(SUBMITTER_USER_TYPE)

  if (includesActivityHubUsers && includesSubmitters) return organizationUsers(state)
  if (includesActivityHubUsers) return activityHubUsers(state)
  if (includesSubmitters) return submitterUsers(state)

  return []
}

export const organizationUsersById = (ids = [], options = { excludeInactive: false }) => (state) => {
  const { excludeInactive } = options

  let users = ids.map((id) => organizationUserById(id)(state))

  if (excludeInactive) {
    users = users.filter((user) => user.active)
  }

  return users
}

export default organizationUsersSlice.reducer
